<script setup lang="ts">
import { onMounted, onUnmounted, reactive, toRaw } from 'vue';
import axios from 'axios';
import type { SizeType } from 'ant-design-vue/es/config-provider';
import { filtrarSelectOptions } from '~/shared/utils/helpers';

const API_URL = import.meta.env.VITE_API_URL;

const props = withDefaults(defineProps<Props>(), {
  emptyMessage: 'Nenhum dado encontrado',
  placeholder: 'Selecione...',
});
const emit = defineEmits(['update:value', 'change']);

interface Props {
  url: string
  allowClear?: boolean
  containerStyle?: any
  disabled?: boolean
  emptyMessage?: string | null
  mode?: 'multiple' | 'tags' | undefined
  placeholder?: string | null
  size?: SizeType
  value: any
  valueIsNumber?: boolean
  variables?: any
}

interface State {
  items: any[]
  loading: boolean
}

const state = reactive<State>({
  items: [],
  loading: false,
});

async function fetchData() {
  state.loading = true;
  try {
    const response = await axios.get(`${API_URL}${props.url}`, {
      params: props.variables,
      getAccessToken: true,
    } as any);

    state.items = (response.data.items || []).map((x: any) => ({
      value: x.value,
      label: x.label?.toUpperCase(),
    }));
  }
  catch {
    state.items = [];
  }
  finally {
    state.loading = false;
  }
}

async function reloadAsync() {
  await fetchData();
  emit('update:value', props.mode !== 'multiple' ? null : []);
  emit('change', props.mode !== 'multiple' ? null : [], toRaw(state.items));
}

async function reset() {
  state.items = [];
  emit('update:value', props.mode !== 'multiple' ? null : []);
  emit('change', props.mode !== 'multiple' ? null : [], toRaw(state.items));
}

async function onChange(value: any) {
  const val = value === undefined ? null : value;
  emit('update:value', val);
  emit('change', val, toRaw(state.items));
}

async function onFocus() {
  if (state.items?.length === 0) {
    await fetchData();
  }
}

function clearItems() {
  state.items = [];
}

function getItems() {
  return toRaw(state.items);
}

onMounted(() => {
  state.items = [];
});

onUnmounted(() => {
  state.items = [];
});

defineExpose({
  clearItems,
  getItems,
  reloadAsync,
  reset,
});
</script>

<template>
  <div :style="props.containerStyle">
    <a-spin
      :spinning="state.loading"
      size="small"
    >
      <a-select
        :allow-clear="props.allowClear"
        :disabled="props.disabled"
        :value="props.value"
        :label-in-value="true"
        :placeholder="props.placeholder"
        :filter-option="filtrarSelectOptions"
        :options="state.items"
        :dropdown-class-name="
          state.loading ? 'ant-select-dropdown-loading' : ''
        "
        :mode="props.mode"
        :default-active-first-option="false"
        :size="props.size"
        :dropdown-style="state.loading ? { display: 'none' } : {}"
        show-search
        style="width: 100%"
        @change="onChange"
        @focus="onFocus"
      >
        <template #notFoundContent>
          <a-empty
            v-if="!state.loading"
            :description="emptyMessage"
          />
        </template>
      </a-select>
    </a-spin>
  </div>
</template>
